/**
 * The "While you were away" modal (M4 spec §13, §15 item 7). The player's own sections come first (D-459 bug 3: eight
 * faction headlines used to bury the fleet): Your fleet (M6 P35, when there is fleet news), Loose ends (M7 S35: story
 * warnings and floors held, when the absence touched the story), then Headlines, Markets, Traffic and New stations, then
 * Continue (Enter, pad A or a click).
 */
import type { ReactNode } from 'react'
import type { AwayReportModel } from './awayReportModel'
import './strategic.css'

export function AwayReport(p: { model: AwayReportModel; onContinue: () => void }): ReactNode {
  const m = p.model
  return (
    <div className="sui sui-away" role="dialog" aria-modal="true" aria-label={m.title} data-testid="away-report">
      <header className="sui-head">
        <h2 className="sui-title">{m.title}</h2>
        <p className="sui-sub">{m.summary}</p>
        {m.capped !== null ? <p className="sui-sub sui-warn">{m.capped}</p> : null}
      </header>
      {m.fleet !== null ? (
        <section className="sui-section" data-testid="away-fleet">
          <h3 className="sui-section__title">{m.fleet.title}</h3>
          {m.fleet.summary.map((r) => (
            <div key={r.label} className="sui-line" data-tone={r.tone}>
              <span className="sui-line__label">{r.label}</span>
              <span className="sui-line__status">{r.value}</span>
            </div>
          ))}
          {m.fleet.groups.length === 0 ? <p className="sui-empty">{m.fleet.empty}</p> : null}
          {m.fleet.groups.map((g) => (
            <div key={g.id} data-group={g.id}>
              <h4 className="sui-section__title">{g.title}</h4>
              <ol className="sui-list">
                {g.lines.map((l) => (
                  <li key={l.key} className="sui-item">
                    <span className="sui-item__headline">{l.text}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </section>
      ) : null}
      {m.story !== null ? (
        <section className="sui-section" data-testid="away-story">
          <h3 className="sui-section__title">{m.story.title}</h3>
          {m.story.warnings.length > 0 ? (
            <div data-group="warnings">
              <h4 className="sui-section__title">{m.story.warningsTitle}</h4>
              <ol className="sui-list">
                {m.story.warnings.map((w) => (
                  <li key={w} className="sui-item sui-warn">
                    <span className="sui-item__headline">
                      {'⚠'} {w}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          {m.story.floors.length > 0 ? (
            <div data-group="floors">
              <h4 className="sui-section__title">{m.story.floorsTitle}</h4>
              <ol className="sui-list">
                {m.story.floors.map((f) => (
                  <li key={f.key} className="sui-item">
                    <span className="sui-item__headline">{f.text}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </section>
      ) : null}
      <section className="sui-section">
        <h3 className="sui-section__title">Headlines</h3>
        {m.headlines.length === 0 ? <p className="sui-empty">{m.empty.headlines}</p> : null}
        <ol className="sui-list">
          {m.headlines.map((r) => (
            <li key={r.id} className="sui-item" data-severity={r.severity}>
              <span className="sui-item__meta">
                {r.severityGlyph} {r.severityLabel} {'·'} {r.dayLabel}
              </span>
              <span className="sui-item__headline">{r.headline}</span>
              {r.cause !== null ? <span className="sui-item__cause">Cause: {r.cause}</span> : null}
            </li>
          ))}
        </ol>
      </section>
      <section className="sui-section">
        <h3 className="sui-section__title">Markets</h3>
        {m.markets.length === 0 ? <p className="sui-empty">{m.empty.markets}</p> : null}
        {m.markets.map((r) => (
          <div key={`${r.ware}-${r.station}`} className="sui-line" data-tone={r.sign > 0 ? 'up' : r.sign < 0 ? 'down' : 'flat'}>
            <span className="sui-line__label">
              {r.ware} at {r.station}
            </span>
            <span className="sui-line__status">{r.prices}</span>
            <span className="sui-line__explain">{r.change}</span>
          </div>
        ))}
      </section>
      <section className="sui-section">
        <h3 className="sui-section__title">Traffic</h3>
        {m.traffic.map((t) => (
          <div key={t.label} className="sui-line">
            <span className="sui-line__label">{t.label}</span>
            <span className="sui-line__status">{t.value}</span>
          </div>
        ))}
      </section>
      <section className="sui-section">
        <h3 className="sui-section__title">New stations</h3>
        {m.newStations.length === 0 ? <p className="sui-empty">{m.empty.newStations}</p> : null}
        {m.newStations.map((s) => (
          <div key={s} className="sui-line">
            <span className="sui-line__label">{s}</span>
          </div>
        ))}
      </section>
      <div className="sui-actions">
        <button type="button" className="sui-btn sui-primary" data-testid="away-continue" onClick={p.onContinue}>
          Continue
        </button>
      </div>
    </div>
  )
}
